import { c as _c } from "react/compiler-runtime";
import { useContext, useEffect } from "react";
import { AuthContext } from "../AuthProvider";
import { useActiveGithubOrgForUserNonSuspense } from "@/hooks/useConnectedGithubOrgs";
import { SIDEBAR_AUTOMATION_ROWS_STORAGE_KEY_PREFIX } from "@/components/codesmith/useCachedSidebarAutomationRows";
import { urlInstanceSlug } from "@/lib/named-org";

// Flags are per org, so entries are keyed by instance and org name, e.g.
// `sidebar-codesmith-enabled:ghe:acme`, to avoid leaking one org's flag into
// another (including a same-named org on another GitHub instance) while orgs
// load. The instance is the one the URL addresses, the same one the org list
// is fetched for.
const CODESMITH_ENABLED_STORAGE_KEY_PREFIX = "sidebar-codesmith-enabled";
const SANDBOXES_ENABLED_STORAGE_KEY_PREFIX = "sidebar-sandboxes-enabled";
// Routes without an org segment (/usage, /settings, /support, /admin, ...)
// have no org to key the lookup with while auth and orgs load, so the last
// resolved org is kept as a pointer for those lookups. The server-side
// active org is what those routes resolve to, and this is the best local
// guess of it.
const LAST_ACTIVE_ORG_STORAGE_KEY = "sidebar-codesmith-last-org";
const LAST_ACTIVE_INSTANCE_STORAGE_KEY = "sidebar-codesmith-last-instance";
// The cache belongs to one account: without this marker, a later session on
// the same browser could adopt the previous account's flag on org-less
// routes via the last-org pointer until orgs resolve.
const CACHE_OWNER_STORAGE_KEY = "sidebar-codesmith-user";
function flagStorageKey(prefix: string, instanceSlug: string, org: string) {
  return `${prefix}:${instanceSlug}:${org}`;
}
export function clearCodesmithLayoutCache() {
  if (typeof window === "undefined") return;
  try {
    const doomed = [LAST_ACTIVE_ORG_STORAGE_KEY, LAST_ACTIVE_INSTANCE_STORAGE_KEY, CACHE_OWNER_STORAGE_KEY];
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key?.startsWith(`${CODESMITH_ENABLED_STORAGE_KEY_PREFIX}:`) || key?.startsWith(`${SANDBOXES_ENABLED_STORAGE_KEY_PREFIX}:`) ||
      // Automation groups are per user, so the next account must not
      // reserve this one's section.
      key?.startsWith(SIDEBAR_AUTOMATION_ROWS_STORAGE_KEY_PREFIX)) {
        doomed.push(key);
      }
    }
    for (const key of doomed) {
      window.localStorage.removeItem(key);
    }
  } catch {
    // Local storage is an optimization; the resolved org setting remains authoritative.
  }
}

/**
 * Last resolved active org, for navigation targets on org-less routes while
 * orgs load. Paired with `getCachedCodesmithEnabled(undefined)`: when the
 * cached flag mounts Codesmith UI before `activeGithubOrg` resolves, this is
 * the org that cached flag belongs to, and org-less paths like
 * `/sessions/new` don't exist (sessions live under `/[org]/sessions`).
 */
export function getCachedLastActiveOrg(): string | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    return window.localStorage.getItem(LAST_ACTIVE_ORG_STORAGE_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}
function readCachedFlag(prefix: string, org: string | null | undefined, instanceSlug: string): boolean | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    // Org-less routes read the last resolved org on the instance it resolved on.
    const key = org ? flagStorageKey(prefix, instanceSlug, org) : lastActiveFlagStorageKey(prefix);
    const value = key === null ? null : window.localStorage.getItem(key);
    return value === null ? undefined : value === "true";
  } catch {
    return undefined;
  }
}
function lastActiveFlagStorageKey(prefix: string): string | null {
  const org = window.localStorage.getItem(LAST_ACTIVE_ORG_STORAGE_KEY);
  const instanceSlug = window.localStorage.getItem(LAST_ACTIVE_INSTANCE_STORAGE_KEY);
  return org && instanceSlug ? flagStorageKey(prefix, instanceSlug, org) : null;
}
export function getCachedCodesmithEnabled(org: string | null | undefined, instanceSlug: string = urlInstanceSlug()): boolean | undefined {
  return readCachedFlag(CODESMITH_ENABLED_STORAGE_KEY_PREFIX, org, instanceSlug);
}
export function getCachedSandboxesEnabled(org: string | null | undefined, instanceSlug: string = urlInstanceSlug()): boolean | undefined {
  return readCachedFlag(SANDBOXES_ENABLED_STORAGE_KEY_PREFIX, org, instanceSlug);
}

/**
 * Codesmith availability for the active org, falling back to the cached
 * last-known value while the org query is in flight so Codesmith-gated UI
 * (the rail row) doesn't pop in after every load.
 * `undefined` means genuinely unknown (first visit).
 */
export function useCodesmithEnabledWithCache(): boolean | undefined {
  const {
    user
  } = useContext(AuthContext);
  const {
    activeGithubOrg,
    codesmithEnabled,
    codesmithEnabledKnown,
    orgsResolved
  } = useActiveGithubOrgForUserNonSuspense();
  // A successful resolve is authoritative in every case: an org in
  // installations carries its real flag, and a missing one (no installations,
  // or a URL org this account can't see) means codesmithEnabled resolved to
  // false, so no cached previous flag applies. Only query errors (rate limit,
  // SSO, network) stay unresolved, so an unknown state never removes the rail
  // (or poisons the cache) as if Codesmith were resolved off.
  const isUnresolved = !orgsResolved;
  const instanceSlug = urlInstanceSlug(undefined, user?.default_instance_slug);
  useEffect(() => {
    if (!user) return;
    try {
      if (window.localStorage.getItem(CACHE_OWNER_STORAGE_KEY) !== String(user.id)) {
        clearCodesmithLayoutCache();
        window.localStorage.setItem(CACHE_OWNER_STORAGE_KEY, String(user.id));
      }
      if (isUnresolved) return;
      if (!activeGithubOrg) {
        // No installations: the last-org pointer names an org this account
        // can no longer see, so drop it before the next load resurrects
        // Codesmith UI (and navigation targets) from that org's cached flag.
        window.localStorage.removeItem(LAST_ACTIVE_ORG_STORAGE_KEY);
        window.localStorage.removeItem(LAST_ACTIVE_INSTANCE_STORAGE_KEY);
        return;
      }
      const flagKey = flagStorageKey(CODESMITH_ENABLED_STORAGE_KEY_PREFIX, instanceSlug, activeGithubOrg);
      if (!codesmithEnabledKnown) {
        // The URL org resolved outside this account's installations: its
        // cached flag is stale (the account can't see the org), and neither
        // caching the fallback false nor repointing the last-org pointer at
        // it would describe an org the server can resolve to. A last-org
        // pointer still naming it is equally stale and would route org-less
        // nav targets to the inaccessible org on a later unresolved load, so
        // it goes too.
        window.localStorage.removeItem(flagKey);
        if (window.localStorage.getItem(LAST_ACTIVE_ORG_STORAGE_KEY) === activeGithubOrg && window.localStorage.getItem(LAST_ACTIVE_INSTANCE_STORAGE_KEY) === instanceSlug) {
          window.localStorage.removeItem(LAST_ACTIVE_ORG_STORAGE_KEY);
          window.localStorage.removeItem(LAST_ACTIVE_INSTANCE_STORAGE_KEY);
        }
        return;
      }
      // Superusers cache the resolved value like everyone else, including
      // the forced true from impersonation: the cache is owner-marked to
      // this account, and the next resolve for the same org forces true
      // again, so the cached flag stays self-consistent for this browser.
      window.localStorage.setItem(flagKey, String(codesmithEnabled));
      window.localStorage.setItem(LAST_ACTIVE_ORG_STORAGE_KEY, activeGithubOrg);
      window.localStorage.setItem(LAST_ACTIVE_INSTANCE_STORAGE_KEY, instanceSlug);
    } catch {
      // Local storage is an optimization; the resolved org setting remains authoritative.
    }
  }, [user, isUnresolved, instanceSlug, activeGithubOrg, codesmithEnabled, codesmithEnabledKnown]);

  // Fall back to the cached value while org data is loading so the Codesmith
  // rail row doesn't pop in or out before the feature query resolves.
  return isUnresolved ? getCachedCodesmithEnabled(activeGithubOrg, instanceSlug) : codesmithEnabled;
}

/**
 * Sandboxes availability for the active org, backed by the same per-account
 * cache as `useCodesmithEnabledWithCache` so the rail row survives the
 * skeleton swap. Unlike [code]smith, Sandboxes is off by default, so an
 * unknown flag hides the row. Call after `useCodesmithEnabledWithCache`,
 * whose effect owns the cache-owner marker this one checks.
 */
export function useSandboxesEnabledWithCache() {
  const $ = _c(15);
  const {
    user
  } = useContext(AuthContext);
  const {
    activeGithubOrg,
    sandboxesEnabled,
    codesmithEnabledKnown: orgKnown,
    orgsResolved
  } = useActiveGithubOrgForUserNonSuspense();
  const t0 = user?.default_instance_slug;
  let t1;
  if ($[0] !== t0) {
    t1 = urlInstanceSlug(undefined, t0);
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const instanceSlug = t1;
  let t2;
  let t3;
  if ($[2] !== activeGithubOrg || $[3] !== instanceSlug || $[4] !== orgKnown || $[5] !== orgsResolved || $[6] !== sandboxesEnabled || $[7] !== user) {
    t2 = () => {
      if (!user || !orgsResolved || !activeGithubOrg) {
        return;
      }
      try {
        if (window.localStorage.getItem(CACHE_OWNER_STORAGE_KEY) !== String(user.id)) {
          return;
        }
        const key = flagStorageKey(SANDBOXES_ENABLED_STORAGE_KEY_PREFIX, instanceSlug, activeGithubOrg);
        if (orgKnown) {
          window.localStorage.setItem(key, String(sandboxesEnabled));
        } else {
          window.localStorage.removeItem(key);
        }
      } catch {}
    };
    t3 = [user, orgsResolved, instanceSlug, activeGithubOrg, orgKnown, sandboxesEnabled];
    $[2] = activeGithubOrg;
    $[3] = instanceSlug;
    $[4] = orgKnown;
    $[5] = orgsResolved;
    $[6] = sandboxesEnabled;
    $[7] = user;
    $[8] = t2;
    $[9] = t3;
  } else {
    t2 = $[8];
    t3 = $[9];
  }
  useEffect(t2, t3);
  let t4;
  if ($[10] !== activeGithubOrg || $[11] !== instanceSlug || $[12] !== orgsResolved || $[13] !== sandboxesEnabled) {
    t4 = orgsResolved ? sandboxesEnabled : getCachedSandboxesEnabled(activeGithubOrg, instanceSlug) === true;
    $[10] = activeGithubOrg;
    $[11] = instanceSlug;
    $[12] = orgsResolved;
    $[13] = sandboxesEnabled;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return t4;
}